import { announcementService } from '../../helpers/authservice/announcement.service';
import router from '../../router/index'
import Swal from 'sweetalert2';

// Laravel 422 bodies are {message, errors: {field: [msg, ...]}}; the notification
// module renders a flat string list, so flatten before handing it over.
const validationMessages = (error) => {
    const data = (error && error.response && error.response.data) || {};

    if (data.errors) {
        return Object.values(data.errors).flat();
    }

    return [data.message || 'Something went wrong. Please try again.'];
}

export const state = {
    announcement: [],
    pagination: {
        per_page: '',
        total: '',
        page: ''
    },
    active: [],
    active_pagination: {
        per_page: '',
        total: '',
        page: ''
    },
    archive: [],
    archive_pagination: {
        per_page: '',
        total: '',
        page: ''
    },
    active_count: 0,
    // Announcements relevant to the camera of the record currently being worked on.
    camera_announcements: [],
    camera_options: [],
}

export const mutations = {
    FETCH_ANNOUNCEMENT: (state, payload) => {
        state.announcement = payload;
    },
    FETCH_PAGINATION: (state, payload) => {
        state.pagination = payload;
    },
    FETCH_ACTIVE_ANNOUNCEMENT: (state, payload) => {
        // state.active = payload;
        payload.forEach(value => {
            state.active.push(value);
        });
    },
    CLEAR_ACTIVE_ANNOUNCEMENT: (state) => {
        state.active = [];
    },
    FETCH_ACTIVE_PAGINATION: (state, payload) => {
        state.active_pagination = payload;
    },
    FETCH_ARCHIVE_ANNOUNCEMENT: (state, payload) => {
        payload.forEach(value => {
            state.archive.push(value);
        });
        // state.archive = payload;
    },
    CLEAR_ARCHIVE_ANNOUNCEMENT: (state) => {
        state.archive = [];
    },
    FETCH_ARCHIVE_PAGINATION: (state, payload) => {
        state.archive_pagination = payload;
    },
    FETCH_ACTIVE_COUNT: (state, payload) => {
        state.active_count = payload;
    },
    FETCH_CAMERA_ANNOUNCEMENTS: (state, payload) => {
        state.camera_announcements = payload;
    },
    FETCH_CAMERA_OPTIONS: (state, payload) => {
        state.camera_options = payload;
    }
}

export const actions = {
    clearAnnouncements: ({ commit, dispatch }) => {
        dispatch('globals/loading', null, { root: true })

        commit('CLEAR_ACTIVE_ANNOUNCEMENT');
        commit('FETCH_ACTIVE_PAGINATION', {
            page: '',
            total: '',
            per_page: ''
        });

        commit('CLEAR_ARCHIVE_ANNOUNCEMENT');
        commit('FETCH_ARCHIVE_PAGINATION', {
            page: '',
            total: '',
            per_page: ''
        });
    },
    getAnnouncements: ({ commit, dispatch }, pagination) => {
        dispatch('globals/loading', null, { root: true })

        announcementService.getAll(pagination)
        .then(({ data, meta }) => {
            commit('FETCH_ANNOUNCEMENT', data);
            commit('FETCH_PAGINATION', {
                page: meta.current_page,
                last_page: meta.last_page,
                total: meta.total,
                per_page: meta.per_page
            });
            dispatch('globals/loaded', null, { root: true })
        })
        .catch((error) => {
            const { message } = error.response.data;
            dispatch('notification/error', message, { root: true });
            dispatch('globals/loaded', null, { root: true })
        })
    },
    getActiveAnnouncements: ({ commit, dispatch }, pagination) => {
        dispatch('globals/loading', null, { root: true })

        announcementService.getActive(pagination)
        .then(({ data, meta }) => {
            commit('FETCH_ACTIVE_ANNOUNCEMENT', data);
            commit('FETCH_ACTIVE_PAGINATION', {
                page: meta.current_page,
                last_page: meta.last_page,
                total: meta.total,
                per_page: meta.per_page
            });
            dispatch('globals/loaded', null, { root: true })
        })
        .catch((error) => {
            const { message } = error.response.data;
            dispatch('notification/error', message, { root: true });
            dispatch('globals/loaded', null, { root: true })
        })
    },
    getArchiveAnnouncements: ({ commit, dispatch }, pagination) => {
        dispatch('globals/loading', null, { root: true })

        announcementService.getArchive(pagination)
        .then(({ data, meta }) => {
            commit('FETCH_ARCHIVE_ANNOUNCEMENT', data);
            commit('FETCH_ARCHIVE_PAGINATION', {
                page: meta.current_page,
                last_page: meta.last_page,
                total: meta.total,
                per_page: meta.per_page
            });
            dispatch('globals/loaded', null, { root: true })
        })
        .catch((error) => {
            const { message } = error.response.data;
            dispatch('notification/error', message, { root: true });
            dispatch('globals/loaded', null, { root: true })
        })
    },
    getActiveCount: ({ commit }) => {
        // Feeds the sidebar badge — deliberately silent so a failure here never
        // blocks the menu from rendering.
        announcementService.getActiveCount()
        .then(({ count }) => {
            commit('FETCH_ACTIVE_COUNT', count);
        })
        .catch(() => {
            commit('FETCH_ACTIVE_COUNT', 0);
        })
    },
    getCameraAnnouncements: ({ commit }, cameraId) => {
        if (!cameraId) {
            commit('FETCH_CAMERA_ANNOUNCEMENTS', []);
            return;
        }

        // Runs alongside the traffic-record queue; must not touch globals/loading
        // or surface a notification, otherwise it hijacks the reviewer's screen.
        return announcementService.getForCamera(cameraId)
        .then(({ data }) => {
            commit('FETCH_CAMERA_ANNOUNCEMENTS', data || []);
        })
        .catch(() => {
            commit('FETCH_CAMERA_ANNOUNCEMENTS', []);
        })
    },
    getCameraOptions: ({ commit, dispatch }) => {
        return announcementService.getCameraOptions()
        .then(({ data }) => {
            commit('FETCH_CAMERA_OPTIONS', data || []);
        })
        .catch((error) => {
            const { message } = error.response.data;
            dispatch('notification/error', message, { root: true });
        })
    },
    createAnnouncement: ({ dispatch }, body) => {
        dispatch('globals/loading', null, { root: true })

        announcementService.create(body)
        .then(() => {
            dispatch('notification/clear', null, { root: true });
            router.push('/announcement/list');
            Swal.fire("Success", `You have created a new announcement ${body.title}` , "success");
            dispatch('globals/loaded', null, { root: true })
        })
        .catch((error) => {
            window.scrollTo(0, 0);
            dispatch('notification/errors', validationMessages(error), { root: true });
            dispatch('globals/loaded', null, { root: true })
         })
    },
    updateAnnouncement: ({ dispatch }, body) => {
        dispatch('globals/loading', null, { root: true })

        announcementService.update(body)
        .then(() => {
            window.scrollTo(0, 0);
            dispatch('notification/success', 'Announcement updated successfully', { root: true });
            dispatch('globals/loaded', null, { root: true })
        })
        .catch((error) => {
           window.scrollTo(0, 0);
           dispatch('notification/errors', validationMessages(error), { root: true });
           dispatch('globals/loaded', null, { root: true })
        })
    },
    deleteAnnouncement: ({ state, dispatch }, id) => {
        dispatch('globals/loading', null, { root: true })

        announcementService.destroy(id)
        .then(() => {
            dispatch('notification/clear', null, { root: true });
            router.push('/announcement/list');
            dispatch('globals/loaded', null, { root: true });
            dispatch('getAnnouncements', state.pagination);
           
        })
        .catch((error) => {
            window.scrollTo(0, 0);
            dispatch('notification/errors', validationMessages(error), { root: true });
            dispatch('globals/loaded', null, { root: true });
         })
    }
}